// =============================================================================
// navigationCoquille.js — la MÉCANIQUE de navigation de la coquille
// (refonte « Mosaïque vivante », lot L6 « Coquille et transitions », 28/09/2026 ;
// synthèse §5.3 règle 5, §6.5 et moment M3 « la tuile devient la page »).
//
// navTo (App.jsx) reste le point de passage UNIQUE de toutes les navigations
// internes. Ce module lui fournit trois choses, sans dépendance hors React :
//
//  1. LE DÉBIT. Le détecteur d'abus d'origine (40 navigations par minute, puis
//     10 s de blocage silencieux) coupait la barre d'onglets et la palette chez
//     un utilisateur rapide. Décision de Pierre n° 9 (27/09/2026) : la limite
//     est relevée pour les SEULES navigations de coquille (rail, onglets,
//     feuille « Modules », palette, raccourcis ⌥1 à ⌥9), qui ont leur propre
//     compteur. Les autres gardent 40.
//  2. LA SOURCE. Une navigation déclenchée au CLAVIER (Entrée dans la palette,
//     ⌥1 à ⌥9, Entrée ou Espace sur un bouton : clic de `detail` 0, ou toute
//     navigation qui suit une touche de moins d'une seconde : Alt+←, Entrée
//     sur un lien du contenu) ne s'anime jamais (règle du régime quotidien) ;
//     un retour arrière au DOIGT non plus (le balayage d'iOS anime déjà). Le
//     suivi de la dernière saisie est UNIQUE : saisieClavierRecente, dans
//     src/mouvement.js.
//  3. LA TRANSITION DE VUE (View Transitions API, 0 Ko). Racine en fondu
//     enchaîné de 160 ms à courbes ÉGALES : en mélange « plus-lighter » (celui
//     de l'API), un pixel identique avant et après reste identique pendant
//     tout le fondu — l'en-tête, le rail et la barre d'onglets ne clignotent
//     pas, seul le contenu change. La tuile du module (rail ou feuille) porte
//     un nom et rejoint la tuile de l'en-tête de module si la page d'arrivée
//     en a une ([data-vt-tuile="<clé>"]), en 200 ms. Une transition en cours
//     est TERMINÉE (skipTransition) avant la suivante, et un clic donné
//     PENDANT la transition (que le navigateur adresse à <html>, sous le
//     calque ::view-transition) est rejoué sur l'élément visé : un clic n'est
//     jamais perdu (§3 bis). Le contenu n'est PAS nommé : le document défile (pas un conteneur
//     de hauteur fixe comme dans le prototype), et l'instantané d'une colonne
//     de plusieurs milliers de pixels coûterait trop cher sur iPad.
//
// Les durées vivent dans src/coquilleStyles.js (règles ::view-transition-*),
// plafonnées à 200 ms par src/mouvement.test.js.
// =============================================================================
import { flushSync } from "react-dom";
import { courbeRessort } from "./mouvement";
import { echelleTexte } from "./echelleTexte";

// ── 1. Débit ────────────────────────────────────────────────────────────────
export const FENETRE_LIMITE_MS = 60 * 1000;
export const LIMITE_CONTENU = 40;    // inchangée (détecteur d'abus d'origine)
export const LIMITE_COQUILLE = 120;  // décision n° 9 : rail, onglets, palette, raccourcis

/** Compteur à fenêtre glissante. `noter(t)` enregistre une navigation et dit
 *  si la limite est dépassée. Pur (l'horloge est passée en argument). */
export function creerLimiteur(limite, fenetreMs = FENETRE_LIMITE_MS) {
  let traces = [];
  return {
    noter(maintenant) {
      const seuil = maintenant - fenetreMs;
      traces = traces.filter((t) => t > seuil);
      traces.push(maintenant);
      return { compte: traces.length, depasse: traces.length > limite };
    },
    compte() { return traces.length; },
  };
}

// ── 2. Source ───────────────────────────────────────────────────────────────
/** Un clic produit par le clavier (Entrée ou Espace sur un bouton) a detail 0. */
export const clicAuClavier = (e) => !!(e && e.type === "click" && e.detail === 0);

/** Options de navTo pour un geste de la coquille. `extra` peut porter
 *  { tuile: Element } : la tuile qui deviendra la tuile de l'en-tête (M3). */
export const optsCoquille = (e, extra) => ({ coquille: true, clavier: clicAuClavier(e), ...(extra || {}) });

/** Pointeur fin (souris, trackpad) : le seul où un retour arrière s'anime. */
export function pointeurFin() {
  try { return !!(window.matchMedia && window.matchMedia("(pointer:fine)").matches); } catch { return true; }
}

/** Faut-il une View Transition ? Pur, testé. `pret` : la vue d'arrivée peut
 *  être rendue d'un coup (son code est chargé) — sinon l'instantané « après »
 *  serait un écran de chargement. */
export function doitAnimerTransition({ api, reduit, clavier, retour, doigt, pret }) {
  if (!api || reduit || clavier) return false;
  if (retour && doigt) return false;
  return pret !== false;
}

// ── 3. Transition de vue ────────────────────────────────────────────────────
export const NOM_TUILE = "aramis-tuile";
let transitionCourante = null;
let derniereTransition = null;   // même finie : le rejeu attend son retrait

export const apiTransitionsDisponible = () =>
  typeof document !== "undefined" && typeof document.startViewTransition === "function";

/** Termine sur-le-champ une transition en cours (l'état final s'affiche). */
export function terminerTransitionEnCours() {
  const vt = transitionCourante;
  transitionCourante = null;
  if (vt) { try { vt.skipTransition(); } catch { /* déjà finie */ } }
}

const nommer = (el, nom) => { if (el && el.style) el.style.viewTransitionName = nom; };

/**
 * Applique `maj` (mise à jour d'état React, SYNCHRONE via flushSync) dans une
 * View Transition, puis remonte en haut de page. Sans l'API, ou si elle lève,
 * applique directement. Retourne true si une transition a été lancée.
 * @param {Function} maj
 * @param {{ tuile?: Element|null, cle?: string|null }} o  tuile de départ et
 *        clé du module d'arrivée (la tuile d'arrivée porte data-vt-tuile=clé)
 */
export function transitionVue(maj, { tuile = null, cle = null, defiler = true } = {}) {
  terminerTransitionEnCours();
  installerRejeuClics();
  const appliquer = () => { flushSync(maj); if (defiler) { try { window.scrollTo(0, 0); } catch { /* jsdom */ } } };
  if (!apiTransitionsDisponible()) { appliquer(); return false; }
  const depart = tuile && tuile.isConnected ? tuile : null;
  nommer(depart, NOM_TUILE);
  let arrivee = null;
  let vt;
  try {
    vt = document.startViewTransition(() => {
      nommer(depart, "");
      appliquer();
      if (depart && cle) {
        arrivee = document.querySelector(`[data-vt-tuile="${cle}"]`);
        nommer(arrivee, NOM_TUILE);
      }
    });
  } catch {
    nommer(depart, "");
    appliquer();
    return false;
  }
  transitionCourante = vt;
  derniereTransition = vt;
  const nettoyer = () => {
    nommer(depart, ""); nommer(arrivee, "");
    if (transitionCourante === vt) transitionCourante = null;
  };
  // .finished rejette si la mise à jour a levé : nettoyer dans les deux cas.
  vt.finished.then(nettoyer, nettoyer);
  if (vt.ready && vt.ready.catch) vt.ready.catch(() => { /* transition sautée : rien à faire */ });
  return true;
}

// ── 3 bis. Un clic pendant la transition n'est jamais perdu ─────────────────
// Pendant une View Transition, le calque ::view-transition couvre la page et
// le navigateur adresse pointerdown et click à <html>, jamais au bouton sous
// le pointeur (relecture L6 : trois clics sur trois perdus, à 20, 60 et
// 120 ms ; `pointer-events:none` sur le calque n'y change rien dans Chrome).
// Écoute unique, en capture sur window : un appui adressé à <html> pendant
// une transition la TERMINE ; le clic qui suit est arrêté, puis REJOUÉ sur
// l'élément réellement sous le pointeur une fois le calque retiré
// (vt.finished). Les autres clics ne sont pas touchés.
const FENETRE_REJEU_MS = 1000;
let appuiPendantTransition = 0;
let rejeuInstalle = false;
export function installerRejeuClics() {
  if (rejeuInstalle || typeof window === "undefined" || typeof document === "undefined") return;
  rejeuInstalle = true;
  const racine = () => document.documentElement;
  window.addEventListener("pointerdown", (e) => {
    if (!transitionCourante || e.target !== racine()) return;
    appuiPendantTransition = Date.now();
    terminerTransitionEnCours();
  }, true);
  window.addEventListener("click", (e) => {
    const recent = Date.now() - appuiPendantTransition < FENETRE_REJEU_MS;
    if (e.target !== racine() || (!transitionCourante && !recent)) return;
    appuiPendantTransition = 0;
    e.stopImmediatePropagation();
    e.preventDefault();
    const vt = transitionCourante || derniereTransition;
    terminerTransitionEnCours();
    const { clientX: x, clientY: y } = e;
    const rejouer = () => {
      const cible = typeof document.elementFromPoint === "function" ? document.elementFromPoint(x, y) : null;
      if (!cible || cible === racine()) return;
      cible.dispatchEvent(new MouseEvent("click", {
        bubbles: true, cancelable: true, view: window, detail: e.detail || 1,
        clientX: x, clientY: y, screenX: e.screenX, screenY: e.screenY, button: e.button,
        altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey,
      }));
    };
    const fin = vt && vt.finished && typeof vt.finished.then === "function" ? vt.finished : Promise.resolve();
    fin.then(rejouer, rejouer);
  }, true);
}

// ── 4. Glissements (pastille du rail, filet du sommaire, pastille d'onglet) ──
// Rien ne glisse au clavier : les appelants consultent saisieClavierRecente
// (src/mouvement.js), suivi unique de la dernière saisie.

/** Rectangle qu'occuperait `el` s'il était logé dans `autre` à la même place
 *  que dans son parent actuel (même décalage) : le point de départ d'un FLIP
 *  entre deux lignes ou deux onglets de même gabarit. */
export function rectHomologue(autre, el) {
  const a = autre.getBoundingClientRect();
  const p = el.parentElement.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return { left: a.left + (r.left - p.left), top: a.top + (r.top - p.top) };
}

/** FLIP : `el` part de `depuis` (rectangle, repère de la fenêtre) et rejoint sa
 *  place en --spring-smooth (380 ms, régime spatial). Divise par le zoom du
 *  body (réglage de taille du texte), comme computeHoverMenuPos. */
export function glisserDepuis(el, depuis, { axe = "y", duree = 380 } = {}) {
  if (!el || !depuis || typeof el.animate !== "function") return null;
  const r = el.getBoundingClientRect();
  const zoom = echelleTexte();
  const d = (axe === "x" ? depuis.left - r.left : depuis.top - r.top) / zoom;
  if (!Number.isFinite(d) || Math.abs(d) < 0.5) return null;
  const t = axe === "x" ? `translateX(${d}px)` : `translateY(${d}px)`;
  try { return el.animate([{ transform: t }, { transform: "none" }], { duration: duree, easing: courbeRessort("doux") }); } catch { return null; }
}

// ── 5. Récents (palette) ────────────────────────────────────────────────────
// Les dernières vues ouvertes sur CET appareil (localStorage, sous try/catch),
// la plus récente d'abord, sans doublon. Aucun écran de connexion ni page
// publique : seulement les vues de la coquille.
const CLE_RECENTS = "aramis_recents";
const HORS_RECENTS = new Set(["page-garde", "aramis-login", "accueil", "screen", "participant", "totp-setup"]);
export const MAX_RECENTS = 6;
export function lireRecents() {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_RECENTS) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, MAX_RECENTS) : [];
  } catch { return []; }
}
export function noterRecent(vue) {
  if (!vue || HORS_RECENTS.has(vue)) return;
  try {
    const l = [vue, ...lireRecents().filter((x) => x !== vue)].slice(0, MAX_RECENTS);
    localStorage.setItem(CLE_RECENTS, JSON.stringify(l));
  } catch { /* stockage indisponible */ }
}

// ── 6. Raccourci ⌥1 à ⌥9, puis ⌥0 (palette et App) ─────────────────────────
/** Raccourci ⌥1 à ⌥9 : index du module visé (0 à 8) ; ⌥0, la touche qui suit
 *  9 sur la rangée des chiffres, vise le dixième (index 9 : Pilotage pour un
 *  administrateur depuis qu'Affaires publiques est un module, 12/10/2026) ;
 *  -1 sinon. Ignore les combinaisons avec ⌘, Ctrl ou Maj, et la frappe dans un
 *  champ (⌥ y tape des caractères : ¡ ™ £ …) sauf dans la palette elle-même. */
export function raccourciModule(e, { dansPalette = false } = {}) {
  if (!e || !e.altKey || e.metaKey || e.ctrlKey || e.shiftKey || e.isComposing) return -1;
  const m = /^Digit([0-9])$/.exec(e.code || "");
  if (!m) return -1;
  const t = e.target;
  const editable = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""));
  if (editable && !dansPalette) return -1;
  return m[1] === "0" ? 9 : Number(m[1]) - 1;
}
/** La touche d'un module dans l'ordre du rail (« 1 » à « 9 », puis « 0 »), ou
 *  null au-delà du dixième. */
export const toucheModule = (i) => (i >= 0 && i < 9 ? String(i + 1) : i === 9 ? "0" : null);
